import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { supabase } from '@/lib/supabaseClient';

const schema = z.object({
  email: z.string().email(),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    // Always show the "sent" state regardless of outcome — don't leak
    // whether an email is registered (standard password-reset UX).
    await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: `${window.location.origin}${prefix}/my/profile`,
    });
    setSent(true);
  };

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-[#E8E8E6]">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-2">
              {t('auth.forgot.title', 'Reset Password')}
            </h1>
            <p className="text-sm text-[#8A8A8A] mb-8">
              {t('auth.forgot.subtitle', 'Enter your email and we will send you instructions')}
            </p>

            {sent ? (
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="w-14 h-14 rounded-full bg-[#F0EFED] flex items-center justify-center">
                  <i className="ri-mail-send-line text-2xl text-[#7B1113]"></i>
                </div>
                <p className="text-sm text-[#2A2A2A] text-center">
                  {t('auth.forgot.sent', 'If an account exists, a reset link has been sent.')}
                </p>
                <Link
                  to={prefix + '/login'}
                  className="text-xs text-[#7B1113] hover:underline"
                >
                  {t('auth.forgot.backToLogin', 'Back to Sign In')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                    {t('auth.forgot.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-[#7B1113]">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap h-12"
                >
                  {isSubmitting ? t('common.loading') : t('auth.forgot.submit', 'Send Reset Link')}
                </button>
              </form>
            )}

            <p className="mt-6 text-center text-xs text-[#8A8A8A]">
              <Link to={prefix + '/login'} className="text-[#7B1113] hover:underline">
                {t('auth.forgot.backToLogin', 'Back to Sign In')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}